import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ReactComponent as ViewIcon } from '../../assets/eye-fill.svg';
import { ReactComponent as MailIcon } from '../../assets/mail.svg';
import { ReactComponent as PencilIcon } from '../../assets/pencil.svg';
import Alert from '../../components/Alert';
import BreadCrumbs from '../../components/BreadCrumbs';
import { DropdownOption } from '../../components/Dropdown';
import Pagination from '../../components/Pagination';
import StatusHandler from '../../components/StatusHandler';
import NewTable, { TableColumn } from '../../components/ui/NewTable';
import {
  ClaimProviderReviewStatus,
  ClaimProviderTable,
} from '../../models/claimProvider';
import { ClaimProvidersToDataTable } from '../../utils/claimProviders';
import useClaimProviders from './useClaimProviders';

const SortOrderOptions: DropdownOption[] = [
  { id: 'desc', value: 'Newest first' },
  { id: 'asc', value: 'Oldest first' },
];

const SortByOptions: DropdownOption[] = [
  { id: 'createdAt', value: 'Created date' },
  { id: 'name', value: 'Name' },
  { id: 'rating', value: 'Rating' },
  { id: 'reviewsCount', value: 'Reviews' },
  { id: 'lastSeenAt', value: 'Last seen' },
  { id: 'reviewStatus', value: 'Review status' },
  { id: 'city', value: 'City' },
];

const ReviewStatusOptions: DropdownOption[] = [
  { id: '', value: 'All review statuses' },
  { id: 'pending', value: 'Pending' },
  { id: 'approved', value: 'Approved' },
  { id: 'rejected', value: 'Rejected' },
  { id: 'duplicate', value: 'Duplicate' },
];

const ClaimedOptions: DropdownOption[] = [
  { id: 'false', value: 'Unclaimed only' },
  { id: 'true', value: 'Claimed only' },
  { id: 'all', value: 'All' },
];

const HasEmailOptions: DropdownOption[] = [
  { id: '', value: 'Any email' },
  { id: 'true', value: 'Has email' },
  { id: 'false', value: 'No email' },
];

const HasInviteSentOptions: DropdownOption[] = [
  { id: '', value: 'Any invite' },
  { id: 'true', value: 'Invite sent' },
  { id: 'false', value: 'No invite' },
];

const BulkReviewStatusOptions: DropdownOption[] = [
  { id: 'approved', value: 'Approve' },
  { id: 'rejected', value: 'Reject' },
  { id: 'duplicate', value: 'Mark duplicate' },
  { id: 'pending', value: 'Mark pending' },
];

const ClaimProviders = () => {
  const currentAction = useRef<string>();
  const activeProvider = useRef<ClaimProviderTable>();
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const [displayAlert, setDisplayAlert] = useState(false);
  const [alertDescription, setAlertDescription] = useState('');
  const [selectedProviders, setSelectedProviders] = useState<
    ClaimProviderTable[]
  >([]);
  const [bulkReviewStatus, setBulkReviewStatus] =
    useState<ClaimProviderReviewStatus>('approved');
  const [filters, setSelectedFilters] = useState<{
    sortOrder?: DropdownOption;
    sortBy?: DropdownOption;
    reviewStatus?: DropdownOption;
    claimed?: DropdownOption;
    hasEmail?: DropdownOption;
    hasInviteSent?: DropdownOption;
    search?: string;
  }>({
    sortOrder: SortOrderOptions[0],
    sortBy: SortByOptions[0],
    claimed: ClaimedOptions[0],
  });
  const [tableData, setTableData] = useState<{
    headers: TableColumn<ClaimProviderTable>[];
    data: ClaimProviderTable[];
  }>({ headers: [], data: [] });

  const {
    providers,
    isLoading,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    applyFilters,
    sendInvite,
    bulkSendInvites,
    bulkSetReviewStatus,
    error,
  } = useClaimProviders({ limit: 20, claimed: false });
  const navigate = useNavigate();

  useEffect(() => {
    if (providers) {
      setTableData(ClaimProvidersToDataTable(providers));
      setSelectedProviders([]);
    }
  }, [providers]);

  const onCloseAlert = () => {
    setDisplayAlert(false);
    activeProvider.current = undefined;
    currentAction.current = undefined;
  };

  const toastBulkResult = (
    label: string,
    result?: { succeeded: number; failed: number; results: Array<{ providerId: number; success: boolean; message: string }> },
  ) => {
    if (!result) {
      toast.error(`Failed to ${label}.`);
      return;
    }
    const total = result.succeeded + result.failed;
    if (result.failed === 0) {
      toast.success(`${label} succeeded for ${result.succeeded} of ${total} providers.`);
      return;
    }
    if (result.succeeded === 0) {
      toast.error(`${label} failed for all ${total} providers.`);
    } else {
      toast.success(`${label} succeeded for ${result.succeeded} of ${total} providers.`);
      toast.error(`${result.failed} failed.`);
    }
    const failures = result.results
      .filter(item => !item.success)
      .slice(0, 3)
      .map(item => `#${item.providerId}: ${item.message}`)
      .join(' | ');
    if (failures) {
      toast.error(failures);
    }
  };

  const handleAlertAction = async () => {
    if (!currentAction.current) {
      return;
    }

    if (currentAction.current === 'mail') {
      const current = activeProvider.current;
      if (!current) return;
      const result = await sendInvite(current.id);
      if (result) {
        toast.success('Invite email sent successfully.');
      } else {
        toast.error('Failed to send invite email.');
      }
      setDisplayAlert(false);
      return;
    }

    if (currentAction.current === 'multi-mail') {
      const result = await bulkSendInvites(
        selectedProviders.map(provider => provider.id),
      );
      toastBulkResult('Invite send', result);
      setSelectedProviders([]);
      setDisplayAlert(false);
      return;
    }

    if (currentAction.current === 'multi-review') {
      const result = await bulkSetReviewStatus(
        selectedProviders.map(provider => provider.id),
        bulkReviewStatus,
      );
      toastBulkResult('Review status update', result);
      setSelectedProviders([]);
      setDisplayAlert(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSelectedFilters(prev => ({ ...prev, search: value }));
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      applyFilters({ search: value || undefined });
    }, 400);
  };

  const handleSortBy = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, sortBy: option }));
    applyFilters({ sortBy: option.id });
  };

  const handleSortOrder = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, sortOrder: option }));
    applyFilters({ sortOrder: option.id });
  };

  const handleReviewStatus = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, reviewStatus: option }));
    applyFilters({
      reviewStatus: option.id || undefined,
    });
  };

  const handleClaimed = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, claimed: option }));
    if (option.id === 'all') {
      applyFilters({ claimed: undefined });
      return;
    }
    applyFilters({ claimed: option.id === 'true' });
  };

  const handleHasEmail = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, hasEmail: option }));
    applyFilters({
      hasEmail: option.id === '' ? undefined : option.id === 'true',
    });
  };

  const handleHasInviteSent = (option: DropdownOption) => {
    setSelectedFilters(prev => ({ ...prev, hasInviteSent: option }));
    applyFilters({
      hasInviteSent: option.id === '' ? undefined : option.id === 'true',
    });
  };

  const handleResetFilters = () => {
    setSelectedFilters({
      sortOrder: SortOrderOptions[0],
      sortBy: SortByOptions[0],
      claimed: ClaimedOptions[0],
      search: '',
    });
    applyFilters(
      {
        sortOrder: 'desc',
        sortBy: 'createdAt',
        claimed: false,
        search: undefined,
        reviewStatus: undefined,
        hasEmail: undefined,
        hasInviteSent: undefined,
      },
      true,
    );
  };

  const handleRowSelection = (selected: boolean, row: ClaimProviderTable) => {
    if (selected) {
      setSelectedProviders(prev => [...prev, row]);
      return;
    }
    setSelectedProviders(prev => prev.filter(provider => provider.id !== row.id));
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedProviders(tableData.data);
      return;
    }
    setSelectedProviders([]);
  };

  const handleViewProvider = (row: ClaimProviderTable) => {
    navigate(`/claim-providers/${row.id}`);
  };

  const handleEditProvider = (row: ClaimProviderTable) => {
    navigate(`/claim-providers/${row.id}/edit`);
  };

  const handleMailAlert = (row: ClaimProviderTable) => {
    setAlertDescription(
      `Send claim invite email to "${row.name}"${
        row.emails !== '-' ? ` (${row.emails})` : ''
      }?`,
    );
    setDisplayAlert(true);
    activeProvider.current = row;
    currentAction.current = 'mail';
  };

  const handleBulkMailAlert = () => {
    setAlertDescription(
      `Send claim invite emails to ${selectedProviders.length} selected providers?`,
    );
    setDisplayAlert(true);
    currentAction.current = 'multi-mail';
  };

  const handleBulkReviewAlert = (status: ClaimProviderReviewStatus) => {
    setBulkReviewStatus(status);
    setAlertDescription(
      `Set review status to "${status}" for ${selectedProviders.length} selected providers?`,
    );
    setDisplayAlert(true);
    currentAction.current = 'multi-review';
  };

  const renderActions = (row: ClaimProviderTable) => (
    <>
      <ViewIcon
        title="View provider"
        className="mr-2 fill-[#ffc904] cursor-pointer"
        onClick={() => handleViewProvider(row)}
      />
      <PencilIcon
        title="Edit provider"
        className="mr-2 fill-primary-main cursor-pointer"
        onClick={() => handleEditProvider(row)}
      />
      <MailIcon
        title="Send invite email"
        className={`mr-2 ${
          row.emails === '-'
            ? 'fill-[#939090] cursor-not-allowed'
            : 'fill-primary-main cursor-pointer'
        }`}
        onClick={() => {
          if (row.emails === '-') {
            toast.error('This provider has no email address.');
            return;
          }
          handleMailAlert(row);
        }}
      />
    </>
  );

  const renderSearch = [
    {
      id: 1,
      placeholder: 'Search name, email, phone, website',
      value: filters?.search,
      onInput: handleSearchChange,
    },
  ];

  const renderFilters = [
    {
      id: 1,
      label: 'Sort by',
      defaultValue: filters?.sortBy,
      options: SortByOptions,
      action: handleSortBy,
    },
    {
      id: 2,
      label: 'Order',
      defaultValue: filters?.sortOrder,
      options: SortOrderOptions,
      action: handleSortOrder,
    },
    {
      id: 3,
      label: 'Claimed',
      defaultValue: filters?.claimed,
      options: ClaimedOptions,
      action: handleClaimed,
    },
    {
      id: 4,
      label: 'Review status',
      defaultValue: filters?.reviewStatus,
      options: ReviewStatusOptions,
      action: handleReviewStatus,
    },
    {
      id: 5,
      label: 'Email',
      defaultValue: filters?.hasEmail,
      options: HasEmailOptions,
      action: handleHasEmail,
    },
    {
      id: 6,
      label: 'Invite',
      defaultValue: filters?.hasInviteSent,
      options: HasInviteSentOptions,
      action: handleHasInviteSent,
    },
  ];

  const downloadCSV = {
    fileName: 'Claim_Providers',
    data: selectedProviders.length
      ? selectedProviders.map(({ raw, actions, ...row }) => row)
      : tableData.data.map(({ raw, actions, ...row }) => row),
  };

  const breadCrumbs = [{ displayName: 'Claim Provider' }];

  return (
    <>
      {displayAlert && (
        <Alert
          onClose={onCloseAlert}
          onConfirm={handleAlertAction}
          title={alertDescription}
        />
      )}
      <div className="flex flex-col w-full h-full overflow-auto px-6 scrollbar-hide">
        <h1 className="text-secondary-main text-[50px] my-6">Claim Provider</h1>
        <BreadCrumbs breadcrumbs={breadCrumbs} />
        {selectedProviders.length >= 1 && (
          <div className="flex flex-wrap justify-end gap-2 mb-2">
            <button
              className="flex items-center p-2"
              onClick={handleBulkMailAlert}>
              <MailIcon className="fill-primary-main mr-1" />
              <span className="text-primary-main font-semibold">
                Send invites ({selectedProviders.length})
              </span>
            </button>
            {BulkReviewStatusOptions.map(option => (
              <button
                key={option.id}
                className="flex items-center p-2 border border-secondary-main rounded-md"
                onClick={() =>
                  handleBulkReviewAlert(
                    option.id as ClaimProviderReviewStatus,
                  )
                }>
                <span className="text-secondary-main font-semibold">
                  {option.value}
                </span>
              </button>
            ))}
          </div>
        )}
        <StatusHandler isLoading={isLoading} error={error}>
          <>
            <NewTable
              selectable
              getActions={renderActions}
              selectedRows={selectedProviders}
              onRowSelection={handleRowSelection}
              onSelectAll={handleSelectAll}
              data={tableData?.data}
              columns={tableData?.headers}
              currentPage={currentPage}
              filterable
              filters={renderFilters}
              searchable
              search={renderSearch}
              reset={handleResetFilters}
              downloadCSV={downloadCSV}
            />
            <Pagination
              className="my-4"
              currentPage={currentPage}
              totalPages={totalPages}
              nextPage={nextPage}
              prevPage={prevPage}
            />
          </>
        </StatusHandler>
      </div>
    </>
  );
};

export default ClaimProviders;
